// noinspection JSIgnoredPromiseFromCall

import * as React from "react";
import { useSelector } from "react-redux";
import { signInWithRedirect, signOut as amplifySignOut } from "aws-amplify/auth";
import logoImage from "./llts-logo.png";
import styles from "./AppHeader.module.scss";
import { ApplicationState } from "../../store/store";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import AppBar from "@mui/material/AppBar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { Logout } from "@mui/icons-material";

const UsFlagIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 7410 3900" width="32" height="16">
    <rect width="7410" height="3900" fill="#b22234" />
    <path d="M0,450H7410m0,600H0m0,600H7410m0,600H0m0,600H7410m0,600H0" stroke="#fff" strokeWidth="300" />
    <rect width="2964" height="2100" fill="#3c3b6e" />
    <g fill="#fff">
      <g id="s18">
        <g id="s9">
          <g id="s5">
            <g id="s4">
              <path id="s" d="M247,90 317.34,307.082 132.98,172.918H361.02L176.66,307.082z" />
              <use xlinkHref="#s" y="420" />
              <use xlinkHref="#s" y="840" />
              <use xlinkHref="#s" y="1260" />
            </g>
            <use xlinkHref="#s" y="1680" />
          </g>
          <use xlinkHref="#s4" x="247" y="210" />
        </g>
        <use xlinkHref="#s9" x="494" />
      </g>
      <use xlinkHref="#s18" x="988" />
      <use xlinkHref="#s9" x="1976" />
      <use xlinkHref="#s5" x="2470" />
    </g>
  </svg>
);

const UkFlagIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" width="32" height="16">
    <clipPath id="ukClip">
      <path d="M0,0 v30 h60 v-30 z" />
    </clipPath>
    <clipPath id="ukClip2">
      <path d="M30,15 h30 v15 z v-15 h-30 z h-30 v15 z v-15 h30 z" />
    </clipPath>
    <g clipPath="url(#ukClip)">
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 60,30 M60,0 0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 60,30 M60,0 0,30" clipPath="url(#ukClip2)" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </g>
  </svg>
);

const INSTANCE_US = "US";
const INSTANCE_UK = "UK";

const AppHeader: React.FC = () => {
  const { username, email } = useSelector((state: ApplicationState) => ({
    username: state.userSession.username,
    email: state.userSession.email
  }));
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  const signIn = React.useCallback(() => {
    signInWithRedirect({ provider: { custom: import.meta.env.VITE_IDP_PROVIDER_NAME } });
  }, []);

  const signOut = React.useCallback(() => {
    amplifySignOut();
  }, []);

  const onAvatarClick = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const closeMenu = React.useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const selectedInstance = import.meta.env.VITE_RESTRICT_TO_LLTS_BRANCH === "en-gb" ? INSTANCE_UK : INSTANCE_US;

  React.useEffect(() => {
    document.title = `LLTS - Admin Console (${selectedInstance})`;
  }, [selectedInstance]);

  const onInstanceChange = React.useCallback(
    (event: SelectChangeEvent) => {
      const value = event.target.value;
      if (value === selectedInstance) {
        return;
      }
      const targetUrl =
        value === INSTANCE_UK ? import.meta.env.VITE_UK_ADMIN_CONSOLE_URL : import.meta.env.VITE_US_ADMIN_CONSOLE_URL;
      if (targetUrl) {
        window.location.href = targetUrl;
      }
    },
    [selectedInstance]
  );

  return (
    <AppBar position="sticky" sx={{ bgcolor: "white" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={5}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <img src={logoImage} alt="LanguageLine Solutions" />
          <Typography variant="h5" color="black">
            Admin Console
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ pr: 5 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <Select
              value={selectedInstance}
              onChange={onInstanceChange}
              renderValue={value => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  {value === INSTANCE_US ? <UsFlagIcon /> : <UkFlagIcon />}
                  <span>
                    {value === INSTANCE_US ? "LanguageLine Translation Solutions (US)" : "LanguageLine Solutions (UK)"}
                  </span>
                </Stack>
              )}
            >
              <MenuItem value={INSTANCE_US}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <UsFlagIcon />
                  <span>LanguageLine Translation Solutions (US)</span>
                </Stack>
              </MenuItem>
              <MenuItem value={INSTANCE_UK}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <UkFlagIcon />
                  <span>LanguageLine Solutions (UK)</span>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>
          {!username && (
            <Button onClick={signIn} className={styles.signInBtn}>
              Sign in
            </Button>
          )}
          {username && (
            <IconButton onClick={onAvatarClick}>
              <Avatar>{username?.substring(0, 1).toUpperCase() || email?.substring(0, 1).toUpperCase()}</Avatar>
            </IconButton>
          )}
        </Stack>
      </Stack>
      <div className={styles.headerDividerContainer}>
        <div className={styles.headerDividerBox} />
      </div>

      <Menu
        anchorEl={menuAnchorEl}
        id="account-menu"
        open={!!menuAnchorEl}
        onClose={closeMenu}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1
            }
          }
        }}
      >
        <MenuItem disabled={true}>
          <Avatar /> {username || email}
        </MenuItem>
        <MenuItem onClick={signOut}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </AppBar>
  );
};

export default AppHeader;
